// src/components/multiplayer/MultiplayerLobby.tsx
// Consolidated multiplayer lobby component with inline styles

import React, { useState, CSSProperties } from "react";
import { CreateGamePanel, GameConfiguration } from "./CreateGamePanel.tsx";
import { HostLobbyPanel } from "./HostLobbyPanel.tsx";
import { JoinGamePanel } from "./JoinGamePanel.tsx";
import { OnlineUsersPanel } from "./OnlineUsersPanel.tsx";

// ============================================
// STYLES
// ============================================

const styles: Record<string, CSSProperties> = {
  // Main container
  container: {
    minHeight: "100vh",
    backgroundColor: "#f5f5f5",
  },

  // Connection banner
  connectionBanner: {
    padding: "12px",
    textAlign: "center",
    fontWeight: 600,
  },

  // Error banner
  errorBanner: {
    padding: "12px",
    textAlign: "center",
    backgroundColor: "#f8d7da",
    borderBottom: "2px solid #f5c6cb",
    color: "#721c24",
    fontWeight: 600,
  },

  // Layout container
  layout: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    padding: "20px",
    maxWidth: "1800px",
    margin: "0 auto",
  },

  // Top row - 3 panels
  topRow: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "20px",
    minHeight: "400px",
  },

  // Bottom row - full width
  bottomRow: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "20px",
    minHeight: "300px",
  },

  // Panel card base
  panelCard: {
    display: "flex",
    flexDirection: "column",
    background: "white",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
    overflow: "hidden",
    transition: "all 0.3s ease",
  },

  // Panel header base
  panelHeader: {
    padding: "16px 20px",
    fontWeight: 700,
    fontSize: "18px",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  // Panel header title
  panelTitle: {
    margin: 0,
    fontSize: "18px",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  // Panel content
  panelContent: {
    flex: 1,
    padding: "20px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },

  // Scrollable panel content
  panelContentScrollable: {
    flex: 1,
    padding: "20px",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
  },

  // Back button container
  backButtonContainer: {
    padding: "20px",
    textAlign: "center",
  },

  // Back button
  backButton: {
    padding: "12px 24px",
    backgroundColor: "#6c757d",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s",
  },
};

// Color themes for panels
const panelThemes = {
  orange: {
    border: "3px solid #ff9800",
    headerBackground: "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)",
    headerBorderBottom: "4px solid #e65100",
  },
  blue: {
    border: "3px solid #2196F3",
    headerBackground: "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)",
    headerBorderBottom: "4px solid #1565C0",
  },
  green: {
    border: "3px solid #4CAF50",
    headerBackground: "linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)",
    headerBorderBottom: "4px solid #2E7D32",
  },
};

// ============================================
// COMPONENT
// ============================================

interface MultiplayerLobbyProps {
  serverUrl: string;
  onCreateGame: (boardSize: number, variantId?: string, gameName?: string) => void;
  onJoinGame: (roomId: string) => void;
  onDeleteGame?: (roomId: string) => void;
  onBack?: () => void;
  currentUsername?: string;
  currentUserId?: string;
  connected?: boolean;
  error?: string | null;
  roomCode?: string | null;
  opponentJoinedRoom?: string | null;
  opponentUsername?: string | null;
}

export const MultiplayerLobby: React.FC<MultiplayerLobbyProps> = ({
  serverUrl,
  onCreateGame,
  onJoinGame,
  onDeleteGame,
  onBack,
  currentUsername = "Guest",
  currentUserId,
  connected = false,
  error: connectionError = null,
  roomCode = null,
  opponentUsername = null,
}) => {
  const [isCreatingGame, setIsCreatingGame] = useState(false);
  const [userVariants] = useState<Array<{ id: string; name: string }>>([]);
  const [hoveredPanel, setHoveredPanel] = useState<string | null>(null);

  // Handle game creation
  const handleCreateGame = (config: GameConfiguration) => {
    setIsCreatingGame(true);

    // Preset mapping for variant names
    const presetNames: Record<string, string> = {
      "0808-001": "Traditional FIDE",
      "0909-001": "Perfect Chess",
      "1010-001": "Breakout Chess",
      "1111-001": "Next Level Chess",
      "1212-001": "Arrival Chess",
    };

    const boardSize = config.presetId === "custom"
      ? 8
      : parseInt(config.presetId.substring(0, 2));

    // Use preset name as variantId for standard games, or custom variantId
    const variantId = config.presetId === "custom"
      ? config.variantId
      : presetNames[config.presetId] || "Standard";
    const gameName = config.gameName;

    onCreateGame(boardSize, variantId, gameName);

    setTimeout(() => setIsCreatingGame(false), 1000);
  };

  const isHosting = !!roomCode;
  const opponentJoined = !!opponentUsername;

  const handleStartGame = () => {
    if (roomCode) onJoinGame(roomCode);
  };

  const handleCancelHosting = () => {
    if (roomCode && onDeleteGame) onDeleteGame(roomCode);
  };

  const handleChallengeUser = (userId: string, username: string) => {
    console.log(`Challenging user: ${username} (${userId})`);
  };

  // Helper to get panel styles with theme
  const getPanelStyle = (theme: keyof typeof panelThemes, panelId: string): CSSProperties => ({
    ...styles.panelCard,
    border: panelThemes[theme].border,
    transform: hoveredPanel === panelId ? "translateY(-2px)" : "none",
    boxShadow: hoveredPanel === panelId
      ? "0 6px 20px rgba(0, 0, 0, 0.15)"
      : "0 4px 12px rgba(0, 0, 0, 0.1)",
  });

  const getHeaderStyle = (theme: keyof typeof panelThemes): CSSProperties => ({
    ...styles.panelHeader,
    background: panelThemes[theme].headerBackground,
    borderBottom: panelThemes[theme].headerBorderBottom,
  });

  return (
    <div style={styles.container}>
      {/* Connection Status Banner */}
      <div
        style={{
          ...styles.connectionBanner,
          backgroundColor: connected ? "#d4edda" : "#fff3cd",
          borderBottom: `2px solid ${connected ? "#c3e6cb" : "#ffc107"}`,
          color: connected ? "#155724" : "#856404",
        }}
      >
        {connected ? "Connected to Server" : "Connecting to Server..."}
      </div>

      {/* Error Banner */}
      {connectionError && (
        <div style={styles.errorBanner}>
          {connectionError}
        </div>
      )}

      {/* Main Layout */}
      <div style={styles.layout}>
        {/* Top Row: Create Game (orange), Host Lobby (blue), Join Game (blue) */}
        <div style={styles.topRow}>
          {/* Create Game Panel - Orange */}
          <div
            style={getPanelStyle("orange", "create")}
            onMouseEnter={() => setHoveredPanel("create")}
            onMouseLeave={() => setHoveredPanel(null)}
          >
            <div style={getHeaderStyle("orange")}>
              <h3 style={styles.panelTitle}>Create Game</h3>
            </div>
            <div style={styles.panelContent}>
              <CreateGamePanel
                onCreateGame={handleCreateGame}
                isCreating={isCreatingGame}
                userVariants={userVariants}
              />
            </div>
          </div>

          {/* Host Lobby Panel - Blue */}
          <div
            style={getPanelStyle("blue", "host")}
            onMouseEnter={() => setHoveredPanel("host")}
            onMouseLeave={() => setHoveredPanel(null)}
          >
            <div style={getHeaderStyle("blue")}>
              <h3 style={styles.panelTitle}>Host Lobby</h3>
            </div>
            <div style={styles.panelContent}>
              <HostLobbyPanel
                isHosting={isHosting}
                roomId={roomCode}
                opponentJoined={opponentJoined}
                opponentUsername={opponentUsername}
                onStartGame={handleStartGame}
                onCancelHosting={handleCancelHosting}
              />
            </div>
          </div>

          {/* Join Game Panel - Blue */}
          <div
            style={getPanelStyle("blue", "join")}
            onMouseEnter={() => setHoveredPanel("join")}
            onMouseLeave={() => setHoveredPanel(null)}
          >
            <div style={getHeaderStyle("blue")}>
              <h3 style={styles.panelTitle}>Join Game</h3>
            </div>
            <div style={styles.panelContentScrollable}>
              <JoinGamePanel
                onJoinGame={onJoinGame}
                serverUrl={serverUrl}
              />
            </div>
          </div>
        </div>

        {/* Bottom Row: Online Players (green) - full width */}
        <div style={styles.bottomRow}>
          <div
            style={getPanelStyle("green", "online")}
            onMouseEnter={() => setHoveredPanel("online")}
            onMouseLeave={() => setHoveredPanel(null)}
          >
            <div style={getHeaderStyle("green")}>
              <h3 style={styles.panelTitle}>Online Players</h3>
            </div>
            <div style={styles.panelContentScrollable}>
              <OnlineUsersPanel
                currentUserId={currentUserId}
                currentUsername={currentUsername}
                serverUrl={serverUrl}
                onChallengeUser={handleChallengeUser}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Back Button */}
      {onBack && (
        <div style={styles.backButtonContainer}>
          <button
            onClick={onBack}
            style={styles.backButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#5a6268";
              e.currentTarget.style.transform = "translateY(-2px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#6c757d";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            Back to Menu
          </button>
        </div>
      )}
    </div>
  );
};

export default MultiplayerLobby;
